import { i18n } from 'next-i18next'
import { AppState } from 'src/app/store'
import { isHtmlTextEmpty } from 'src/core/helpers'
import type { UploadFile } from 'antd/lib/upload/interface'
import { ProjectContentImages, ProjectFormDocumentationFiles } from 'src/core/models'

export const userProjectListSelector = (state: AppState) => state.projects.userProjects
export const userProjectListFilterSelector = (state: AppState) => {
  const filter = state.projects.userProjects
  return { text: filter.filterText, type: filter.filterType }
}

export const createPurchaseOperationSelector = (state: AppState) => state.projects.createPurchaseOperation
export const validateProjectIdSelector = (state: AppState) => state.projects.validateProjectId
export const userProjectOldListSelector = (state: AppState) => state.projects.userProjects.data
export const projectDetailsSelector = (state: AppState) => state.projects.projectDetails
export const transferProjectSelector = (state: AppState) => state.projects.transferProject
export const createBrokerageProjectSelector = (state: AppState) => state.projects.createProject
export const createBrokerageProject = (state: AppState) => state.projects.createProject.response

// Project Form Selectors
export const createBigProjectStateSelector = (state: AppState) => state.projects.createBigProject
export const editBigProjectStateSelector = (state: AppState) => state.projects.editBigProject
export const projectFormStateSelector = (state: AppState) => state.projects.projectForm
export const projectFormCurrentStep = (state: AppState) => state.projects.projectForm.activeStep
export const projectFormKindSelector = (state: AppState) => state.projects.projectForm.kind
export const projectFormLocationTextSelector = (state: AppState) => state.projects.projectForm.locationText
export const projectFormIsActiveSelector = (state: AppState) => state.projects.projectForm.is_active
export const selectedContrubutionProjectType = (state: AppState) => state.projects.projectForm.contributionProjectType
export const isFreeDonationSelectedSelector = (state: AppState) => state.projects.projectForm.contributionProjectType == 'FREE_DONATION'
export const isContributionUnitSelectedSelector = (state: AppState) => state.projects.projectForm.contributionProjectType == 'CONTRIBUTION_UNIT'
export const isVintageSelector = (state: AppState) => state.projects.projectForm.is_vintage
export const isTaxReliefSelector = (state: AppState) => state.projects.projectForm.is_tax_relief
export const vintageItemListSelector = (state: AppState) => state.projects.projectForm.vintage_items
export const currentVintageSelector = (state: AppState) => state.projects.projectForm.currentVintage
export const isPropertyOfClimatetradeSelector = (state: AppState) => {
  return state.projects.projectForm.is_property_of_climatetrade
}

export const projectCurrencyTokenCodeSelected = (state: AppState) => {
  const currencies = state.currencies.list.data
  const selectedCurrencyId = state.projects.projectForm.currency_token
  return currencies?.find(c => c?.id === selectedCurrencyId)?.code || 'EUR'
}

export const isVintageFormErrorVisibleSelector = (state: AppState): boolean => {
  const form = state.projects.projectForm
  return form.is_vintage && form.vintage_items.length === 0
}
export const isProjectDescriptionErrorVisible = (state: AppState): boolean => {
  const form = state.projects.projectForm
  const text = {
    en: form.description_en,
    es: form.description_es,
    fr: form.description_fr,
    ko: form.description_ko,
    de: form.description_de,
    pt: form.description_pt,
    it: form.description_it,
  }[i18n.language]
  return text ? isHtmlTextEmpty(text) : true
}


export const projectFormPicturesConvertedSelector = (state: AppState): UploadFile[] => {
  return state.projects.projectForm.pictures.map((picture, idx) => ({
    uid: `${idx}`,
    name: `project-picture-${idx}`,
    url: picture
  }))
}

export const projectFormPriceSelector = (state: AppState): number | number[] => {
  const form = state.projects.projectForm
  if (form.is_vintage){
    return form.vintage_items.map(item => item.price)
  }
  return form.price
}

export const projectFormContentImagesSelector = (state: AppState): ProjectContentImages  => {
  const form = state.projects.projectForm
  return {
    thumbnail: form.thumbnail,
    cover: form.cover,
    main_image: form.main_image
  }
}

export const documentationFormFilesValues = (state: AppState): ProjectFormDocumentationFiles => {
  const form = state.projects.projectForm
  return {
    credits_certificate: form.credits_certificate,
    pdd: form.pdd,
    validation_report: form.validation_report,
    monitoring_report: form.monitoring_report,
    verification_report: form.verification_report,
    device_registration_request: form.device_registration_request,
    resulting_from_the_issuer: form.resulting_from_the_issuer,
    additional_documentation: form.additional_documentation,
    executive_summary: form.executive_summary,
    protocol_implementation_scheme: form.protocol_implementation_scheme,
    insurance_documentation: form.insurance_documentation
  }
}


export const isTaxDescriptionErrorVisibleSelector = (state: AppState): boolean => {
  const form = state.projects.projectForm
  return (
    !form.tax_info_en &&
    !form.tax_info_es &&
    !form.tax_info_fr &&
    !form.tax_info_ko &&
    !form.tax_info_de &&
    !form.tax_info_pt &&
    !form.tax_info_it
  )
}

export const lastStepProjectFormSelector = (state: AppState): number => {
  const form = state.projects.projectForm
  if (!form.kind) return 1
  else if (form.kind === 'contribution' && form.is_tax_relief) { return 6 }
  return {
    compensation: 7,
    energy: 7,
    contribution: 5,
    biodiversity: 7,
    carbon_forward: 7,
  }[form.kind]
}

export const isActiveDisabledSelector = (state: AppState): boolean => {
  const { is_vintage, price, vintage_items, is_active } = state.projects.projectForm
  if (is_active) return false
  if (!is_vintage && (!price || price <= 0)) return true
  if (is_vintage && vintage_items.length === 0) return true
  if (is_vintage && vintage_items.some(v => !v.price || v.price <= 0)) return true
  return false
}

export const isAddDiscountDisabledSelector = (state: AppState): boolean => {
  const { is_vintage, vintage_items, price, currency_token } = state.projects.projectForm
  if (is_vintage) { return vintage_items.length === 0 || !currency_token }
  return !price || !currency_token
}

export const lessVintagePriceSelector = (state: AppState): number => {
  const { vintage_items, is_vintage, price } = state.projects.projectForm
  if (!is_vintage) {
    if (!price) return 0
    return price
  }
  if (vintage_items.length === 0) return 0
  const lessPrice = vintage_items.reduce((less, vintage) => {
    return vintage.price < less ? vintage.price : less
  }, vintage_items[0].price)
  return lessPrice
}
